import { Component, Input, OnChanges, SimpleChanges, ChangeDetectionStrategy } from '@angular/core';
import { Router } from '@angular/router';

import { ActivityReportExtended, Assignment, EBS, PhotoDocument, VacationRequestExtended, StandaloneMileageMoney, ActivityReportMileageMoneyExtended, EmployeeDetails, CompanyDocument } from '@shared/factories';
import { FormatDatesService, UserService } from '@shared/services';

interface HeaderItemModel {
  icon?:          string;
  label?:         string;
  class?:         string;
  skip?:          boolean;

  value?:         string | number;
  valueClass?:    string;

  children?:      HeaderItemModel[];
  childrenClass?: string;
}

interface HeaderTabModel {
  label:    string;
  active?:  boolean;
  skip?:    boolean;
  children: HeaderItemModel[];
}

@Component({
  selector:      'details-header',
  templateUrl: './details-header.component.html',
  styleUrls:  ['./details-header.component.sass'],
  host: { 'class': 'am-flex-align am-flex-tablet-wrap width-mobile-full gap-m', '[class.width-min-450]': '!customHeader' },
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false
})
export class DetailsHeaderComponent implements OnChanges {
  @Input() entry:        any;
  @Input() useCase:      string;
  @Input() skipAvatar:   boolean;
  @Input() customHeader: string;
  @Input() isInternal:   boolean;
  @Input() loading:      boolean;

  secondaryTitle: string;
  subtitle:       HeaderItemModel[];

  headerItems:    HeaderItemModel[] = [];
  headerTabs:     HeaderTabModel[]  = [];
  constructor(
    private router:      Router,
    private userService: UserService
  ) { }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes?.entry?.firstChange   || changes?.entry?.currentValue?.id   !== changes?.entry?.previousValue?.id   ||
        changes?.loading?.firstChange || changes?.loading?.currentValue     !== changes?.loading?.previousValue) {
      this.prepareHeader();
    }
  }

  getName(): string {
    return this.entry?.external_employee?.name || this.entry?.externalEmployee?.name || this.entry?.external_name || this.entry?.name;
  }

  detailsAvailable(): boolean {
    return this.userService.isInternal && this.useCase !== 'e' && this.entry?.external_employee?.id;
  }

  openExternalDetails(): void {
    if (this.detailsAvailable()) this.router.navigate(['/time-tracking/ee-details', this.entry.external_employee.id]);
  }

  private prepareHeader(): any {
    this.headerItems    = [];
    this.headerTabs     = [];
    this.subtitle       = null;
    this.secondaryTitle = null;
    switch (this.useCase) {
      case 'ar':  { return this.prepareActivityReportHeader(this.entry);         };
      case 'a':   { return this.prepareAssignmentHeader(this.entry);             };
      case 'ebs': { return this.prepareEBSHeader(this.entry);                    };
      case 'pd':  { return this.preparePhotoDocumentHeader(this.entry);          };
      case 'vr':  { return this.prepareVacationRequestHeader(this.entry);        };
      case 'mm':  { return this.prepareMileageMoneyHeader(this.entry);           };
      case 'e':   { return this.prepareExternalDetailsHeader(this.entry);        };
      case 'doc': { return this.prepareCompanyDocumentDetailsHeader(this.entry); };
    };
  }

  private prepareActivityReportBasicHeader(entry: ActivityReportExtended) {
    return [
      { icon: 'icon-user',        label: 'Personalnummer', value: entry?.external_employee.personal_number },
      { icon: 'icon-circle-time', label: 'Zeitraum', childrenClass: 'am-flex-column', children: [
        { class: 'crossed', skip: !this.checkIfValidTimePeriod(entry) || !entry.original_end_date, children: [
          { class: 'word-no-wrap', value: FormatDatesService.formatDate(entry.start_date, 'EEEE dd.MM.yyyy') },
          { class: 'pl5 pr5',      value: '-'                                                        },
          { class: 'word-no-wrap', value: entry.id ? FormatDatesService.formatDate(entry.original_end_date, 'EEEE dd.MM.yyyy') : null }
        ]},
        { class: `${entry.assignment.active && !this.checkIfValidTimePeriod(entry) ? 'color-red' : entry.original_end_date ? 'color-orange': ''}`, children: [
          { class: 'word-no-wrap', value: FormatDatesService.formatDate(entry.start_date, 'EEEE dd.MM.yyyy') },
          { class: 'pl5 pr5',      value: '-'                                                 },
          { class: 'word-no-wrap', value: FormatDatesService.formatDate((!this.checkIfValidTimePeriod(entry) ? entry.original_end_date || entry.end_date : entry.end_date), 'EEEE dd.MM.yyyy') },
        ]}
      ].filter(i => !i.skip)},
      { icon: 'icon-note', label: 'Einsatz',          value: entry.assignment.title                 },
      { icon: 'icon-week', label: 'Kalenderwoche',    value: FormatDatesService.formatDate(entry.start_date, 'ww') },
      { icon: 'icon-date', label: 'Erstellungsdatum', value: FormatDatesService.formatDate(entry.created_at), skip: !entry.created_at},
    ].filter(i => !i.skip);
  }

  private prepareActivityReportHeader(entry: ActivityReportExtended): void {
    if (this.loading) this.headerItems = new Array(5).fill(null);
    else this.headerItems = [...this.prepareActivityReportBasicHeader(entry),
      { icon: 'icon-circle-info', label: 'Status',    valueClass: `status-dot ${this.getExportState(entry.export_state)}`,    skip: !this.isInternal || !entry.archived_at },
      { icon: 'icon-comment',     label: 'Kommentar', value: this.getExportComment(entry.export_state, entry.export_comment), skip: !this.isInternal || !entry.archived_at },
    ].filter((i: any) => !i.skip);
  }

  private prepareMileageMoneyHeader(entry: ActivityReportMileageMoneyExtended | StandaloneMileageMoney): void {
    if (this.loading) this.headerItems = new Array(5).fill(null);
    else this.headerItems = [
      { icon: 'icon-user',          label: 'Personalnummer', value: entry.externalEmployee.personal_number },
      { icon: 'icon-circle-time',   label: 'Zeitraum', children: [
        { class: 'word-no-wrap',  value: FormatDatesService.formatDate(entry.startDate, 'EEEE dd.MM.yyyy') },
        { class: 'pl-xxs pr-xxs', value: '-'                                                               },
        { class: 'word-no-wrap',  value: FormatDatesService.formatDate(entry.endDate,   'EEEE dd.MM.yyyy') }
      ]},
      { icon: 'icon-note',          label: 'Einsatz',        value: entry.assignment.title },
      { icon: 'icon-week',          label: 'Kalenderwoche',  value: FormatDatesService.formatDate(entry.startDate, 'ww') },
      { icon: 'icon-licence-plate', label: 'Kennzeichen',    value: (entry as ActivityReportMileageMoneyExtended).licensePlateMapped, skip: !(entry as ActivityReportMileageMoneyExtended).licensePlateMapped }
    ].filter((i: any) => !i.skip);
  }

  private prepareAssignmentHeader(entry: Assignment): void {
    if (this.loading) this.headerItems = new Array(3).fill(null);
    else this.headerItems = [
      { icon: 'icon-note', label: 'Einsatz',       value: entry.title                                                                                                 },
      { icon: 'icon-date', label: 'Einsatz seit',  value: FormatDatesService.formatDate(entry.starts_at)                                                              },
      { icon: 'icon-date', label: 'Einsatz endet', value: FormatDatesService.formatDate(entry.ends_at), class: this.checkExpiryDate(entry.ends_at) ? 'color-red' : '' },
    ];
  }

  private prepareEBSHeader(entry: EBS): void {
    if (this.loading) this.headerItems = new Array(3).fill(null);
    else {
      this.headerItems = [
        { icon: 'icon-note',    label: 'Einsatz',     value: entry.title },
        { icon: 'icon-comment', label: 'Einspiel ID', value: entry.id    }
      ];
      if (entry.assignment_provisioned_on) this.headerItems.push({ icon: 'icon-circle-info', label: 'Bereitstellungsdatum', value: FormatDatesService.formatDate(entry.assignment_provisioned_on) });
    }
  }

  private preparePhotoDocumentHeader(entry: PhotoDocument): void {
    if (this.loading) this.headerItems = new Array(3).fill(null);
    else this.headerItems = [
      { icon: 'icon-user',    label: 'Personalnummer', value: entry.externalEmployee.personal_number                             },
      { icon: 'icon-date',    label: 'Eingang',        value: FormatDatesService.formatDate(entry.createdAt, 'dd.MM.yyyy HH:mm') },
      { icon: 'icon-comment', label: 'zvoove Status',  value: entry.releasedToZvooveStatus                                       }
    ];
  }

  private prepareVacationRequestHeader(entry: VacationRequestExtended): void {
    if (this.loading) this.headerItems = new Array(4).fill(null);
    else {
      let multipleAssignments = entry.involvedAssignments?.length > 1;
      this.headerItems = [
        { icon: 'icon-user',           label: 'Personalnummer',                     value: entry.externalEmployee?.personal_number                                                                 },
        { icon: 'icon-note',           label: multipleAssignments ? '' : 'Einsatz', value: multipleAssignments ? 'Es liegen mehrere Einsätze vor' : entry.assignment?.title || 'Kein Einsatz vorliegend',
          class: 'tooltip-hover', childrenClass: 'web-tooltip font-s white left-10 top-20 width-max-content', children: multipleAssignments ? entry.involvedAssignments?.map(a => (
            { class: 'am-flex-align', value: a.title }
          )) : null
        },
        { icon: 'icon-date',            label: 'Erstellungsdatum', value: FormatDatesService.formatDate(entry.createdAt, 'dd.MM.yyyy HH:mm'), skip: !entry.createdAt                                    },
        { icon: 'icon-circle-question', label: 'Grund',            value: entry.reasonMapped,                                                 skip: !this.isInternal || !entry.reasonMapped             },
        { icon: 'icon-circle-info',     label: 'Initiator',        value: entry.initiatorMapped,                                              skip: !this.isInternal ||  entry.leaveType !== 'overtime' },
        { icon: 'icon-comment',         label: 'Exportstatus',     value: this.getExportComment(entry.exportState, entry.exportComment),      skip: !this.isInternal || !entry.internalReview           },
      ].filter((i: any) => !i.skip);
    }
  }

  private prepareExternalDetailsHeader(entry: EmployeeDetails): void {
    if (this.loading) this.headerItems = new Array(5).fill(null);
    else {
      if (entry?.lastConnection?.lastLoginAt) this.secondaryTitle = `Letzter Login: ${FormatDatesService.formatDate(entry.lastConnection.lastLoginAt)}`;
      this.subtitle = [
        { icon: 'icon-hash', label: 'Personalnummer', value: entry.personal_number           || '-' },
        { icon: 'icon-note', label: 'Niederlassung',  value: entry.workLocation?.description ? `${entry.workLocation?.description} (${entry.workLocation?.identifier})` : '-' },
      ];
      this.headerTabs = [
        {
          label: 'Stammdaten',
          children: [
            { icon: 'icon-phone',              label: 'Mobil',             value: entry.phoneNumber                                        || '-' },
            { icon: 'icon-db',                 label: 'Datenquelle',       value: entry.dataSources?.length && entry.dataSources[0]?.label || '-' },
            { icon: 'icon-source_environment', label: 'Mandant',           value: entry.tenant?.label                                      || '-' },
            { icon: 'icon-users',              label: 'Alt. Pers.-Konten', value: entry.altPersonalAccounts?.join(', ')                    || '-' },    
          ]
        }, {
          label: 'Konto',
          children: [
            { icon: 'icon-date',        label: 'Eingespielt', value: FormatDatesService.formatDate(entry.createdAt)   },
            { icon: 'icon-circle-tick', label: 'Aktiviert',   value: FormatDatesService.formatDate(entry.activatedAt) },
            { icon: 'icon-case',        label: 'Eintritt',    value: FormatDatesService.formatDate(entry.entryDate)   },
            { label: 'Austritt',
              icon: this.externalFired(entry.exitDate)  ? 'icon-warning' : 'icon-door-open',
              value: FormatDatesService.formatDate(entry.exitDate),
              class: this.externalFired(entry.exitDate) ? 'color-orange' : ''
            }
          ]
        }, {
          label: 'App',
          skip:  !entry.lastConnection,
          children: [
            { icon: 'icon-branch',       label: 'App-Version', value: entry.lastConnection?.appVersion   || '-' },
            { icon: 'icon-manufacturer', label: 'Hersteller',  value: entry.lastConnection?.manufacturer || '-' },
            { icon: 'icon-smartphone',   label: 'Modell',      value: entry.lastConnection?.model        || '-' },
          ]
        }
      ].filter(t => !t.skip);
      this.selectTab(this.headerTabs[0]);
    }
  }

  selectTab(tab: HeaderTabModel): void {
    this.headerTabs.forEach(t => t.active = t === tab);
  }

  private prepareCompanyDocumentDetailsHeader(entry: CompanyDocument): void {
    if (this.loading) this.headerItems = new Array(5).fill(null);
    else this.headerItems = [
      { icon: 'icon-date',        label: 'Eingang',   value: FormatDatesService.formatDate(entry.versionDate) },
      { icon: 'icon-branch',      label: 'Version',   value: entry.version || '-'                             },
      { icon: 'icon-circle-tick', label: 'Bestätigt', value: FormatDatesService.formatDate(entry.confirmedAt) },
      { icon: 'icon-note',        label: 'Typ',       value: entry.title   || '-'                             },
    ];
  }

  private externalFired(exitDate: Date): boolean {
    return exitDate && exitDate < new Date();
  }

  private checkIfValidTimePeriod(entry: ActivityReportExtended): boolean {
    return entry.assignment.ends_at.getTime() >= entry.start_date.getTime();
  }

  private getExportComment(exportState: string, exportComment: string): string {
    switch (exportState) {
      case 'ready_to_export':    { return 'Übertragung ausstehend';                         };
      case 'successful_export':  { return 'Übertragung erfolgreich';                        };
      case 'failed_export':      { return  exportComment;                                   };
      case 'resolved_manually':  { return 'Manuell erledigt';                               };
      case 'resolved_by_system': { return 'automatisch erledigt nach 8 Wochen Inaktivität'; };
      default:                   { return 'Übertragung ausstehend';                         };
    };
  }

  private getExportState(importState: string): string {
    switch (importState) {
      case 'ready_to_export':    { return 'grey';   };
      case 'successful_export':  { return 'green';  };
      case 'failed_export':      { return 'red';    };
      case 'resolved_manually':  { return 'blue';   };
      case 'resolved_by_system': { return 'orange'; };
      default:                   { return 'grey';   };
    };
  }

  private checkExpiryDate(endDate: Date): boolean {
    return (endDate.getTime() - new Date().getTime()) < (15 * 24 * 60 * 60 * 1000);
  }

}
